import { createBrowserRouter, createMemoryRouter, Navigate, RouterProvider } from 'react-router-dom';
import { AuthenticatedRoute, AppReadyRoute, GuestRoute } from 'core/routing/guards';
import { paths } from 'core/routing/paths';
import { ErrorBoundary } from 'core/errors/ErrorBoundary';
import { PublicLayout } from 'layout/PublicLayout';
import { AuthLayout } from 'layout/AuthLayout';
import { DashboardLayout } from 'layout/DashboardLayout';
import { SystemLayout } from 'layout/SystemLayout';
import { WelcomePage } from 'features/welcome/WelcomePage';
import { PhoneAuthPage } from 'features/auth/PhoneAuthPage';
import { OtpVerifyPage } from 'features/auth/OtpVerifyPage';
import { CompleteProfilePage } from 'features/profile/CompleteProfilePage';
import { LegalAcceptancePage } from 'features/legal/LegalAcceptancePage';
import { LegalDocumentPage } from 'features/legal/LegalDocumentPage';
import { HomePage } from 'features/home/HomePage';
import { AssessmentIntroPage } from 'features/assessment/AssessmentIntroPage';
import { AssessmentPage } from 'features/assessment/AssessmentPage';
import { ResultPage } from 'features/results/ResultPage';
import { HistoryPage } from 'features/results/HistoryPage';
import { ConsultationsPage } from 'features/consultations/ConsultationsPage';
import { NewConsultationPage } from 'features/consultations/NewConsultationPage';
import { ConsultationDetailPage } from 'features/consultations/ConsultationDetailPage';
import { NotificationsPage } from 'features/notifications/NotificationsPage';
import { SupportPage } from 'features/support/SupportPage';
import { NewTicketPage } from 'features/support/NewTicketPage';
import { TicketDetailPage } from 'features/support/TicketDetailPage';
import { ProfilePage } from 'features/profile/ProfilePage';
import { AccountDeletionPage } from 'features/account-deletion/AccountDeletionPage';
import { ForbiddenPage } from 'features/system/ForbiddenPage';
import { NotFoundPage } from 'features/system/NotFoundPage';
import { ConflictPage } from 'features/system/ConflictPage';
import { RateLimitedPage } from 'features/system/RateLimitedPage';
import { CompanyMemberRoute } from 'core/routing/companyGuards';
import { CompanyCodePage } from 'features/assessment/CompanyCodePage';
import { CompanyCodesPage } from 'features/company/CompanyCodesPage';
import { CompanyCreditsPage } from 'features/company/CompanyCreditsPage';
import { CompanyDashboardPage } from 'features/company/CompanyDashboardPage';
import { CompanyOnboardingPage } from 'features/company/CompanyOnboardingPage';
import { CompanyParticipantResultPage } from 'features/company/CompanyParticipantResultPage';
import { CompanyParticipantsPage } from 'features/company/CompanyParticipantsPage';
import { CompanyPlanDetailPage } from 'features/company/CompanyPlanDetailPage';
import { CompanyPlansPage } from 'features/company/CompanyPlansPage';
import { CompanyPortalPlansPage } from 'features/company/CompanyPortalPlansPage';
import { CompanyPurchaseDetailPage } from 'features/company/CompanyPurchaseDetailPage';
import { CompanyPurchasesPage } from 'features/company/CompanyPurchasesPage';
import { CompanyPurchaseStatusPage } from 'features/company/CompanyPurchaseStatusPage';
import { CompanySettingsPage } from 'features/company/CompanySettingsPage';
import { CompanyLayout } from 'layout/CompanyLayout';

export const appRouteObjects = [
  {
    element: <PublicLayout />,
    children: [
      { path: paths.root, element: <GuestRoute><WelcomePage /></GuestRoute> },
      { path: paths.welcome, element: <GuestRoute><WelcomePage /></GuestRoute> },
      { path: paths.terms, element: <LegalDocumentPage type="terms" /> },
      { path: paths.privacy, element: <LegalDocumentPage type="privacy" /> },
      { path: paths.companyPlans, element: <CompanyPlansPage /> },
      { path: '/company-plans/:planId', element: <CompanyPlanDetailPage /> },
    ],
  },
  {
    element: <AuthLayout />,
    children: [
      { path: paths.auth, element: <GuestRoute><PhoneAuthPage /></GuestRoute> },
      { path: paths.verify, element: <GuestRoute><OtpVerifyPage /></GuestRoute> },
      { path: paths.completeProfile, element: <CompleteProfilePage /> },
    ],
  },
  {
    element: <AuthenticatedRoute><AuthLayout /></AuthenticatedRoute>,
    children: [
      { path: paths.legalAcceptance, element: <LegalAcceptancePage /> },
      { path: paths.accountDelete, element: <AccountDeletionPage /> },
    ],
  },
  {
    element: <AppReadyRoute><DashboardLayout /></AppReadyRoute>,
    children: [
      { path: paths.home, element: <HomePage /> },
      { path: paths.assessment, element: <Navigate to={paths.assessmentIntro} replace /> },
      { path: paths.assessmentIntro, element: <AssessmentIntroPage /> },
      { path: '/assessment/:attemptId', element: <AssessmentPage /> },
      { path: paths.results, element: <Navigate to={paths.history} replace /> },
      { path: '/results/:attemptId', element: <ResultPage /> },
      { path: paths.history, element: <HistoryPage /> },
      { path: paths.consultations, element: <ConsultationsPage /> },
      { path: paths.consultationNew, element: <NewConsultationPage /> },
      { path: '/consultations/:id', element: <ConsultationDetailPage /> },
      { path: paths.notifications, element: <NotificationsPage /> },
      { path: paths.support, element: <SupportPage /> },
      { path: paths.supportNew, element: <NewTicketPage /> },
      { path: '/support/:id', element: <TicketDetailPage /> },
      { path: paths.profile, element: <ProfilePage /> },
      { path: paths.companyCodeEntry, element: <CompanyCodePage /> },
    ],
  },
  {
    element: <AppReadyRoute><CompanyMemberRoute><CompanyLayout /></CompanyMemberRoute></AppReadyRoute>,
    children: [
      { path: paths.companyPortal, element: <CompanyDashboardPage /> },
      { path: paths.companyPortalPlans, element: <CompanyPortalPlansPage /> },
      { path: paths.companyPurchases, element: <CompanyPurchasesPage /> },
      { path: '/company/purchases/:purchaseId', element: <CompanyPurchaseDetailPage /> },
      { path: '/company/purchases/:purchaseId/status', element: <CompanyPurchaseStatusPage /> },
      { path: paths.companyCredits, element: <CompanyCreditsPage /> },
      { path: paths.companyCodes, element: <CompanyCodesPage /> },
      { path: paths.companyParticipants, element: <CompanyParticipantsPage /> },
      { path: '/company/participants/:attemptId/result', element: <CompanyParticipantResultPage /> },
      { path: paths.companySettings, element: <CompanySettingsPage /> },
    ],
  },
  {
    element: <AppReadyRoute><AuthLayout /></AppReadyRoute>,
    children: [
      { path: paths.companyOnboarding, element: <CompanyOnboardingPage /> },
    ],
  },
  {
    element: <SystemLayout />,
    children: [
      { path: paths.forbidden, element: <ForbiddenPage /> },
      { path: paths.conflict, element: <ConflictPage /> },
      { path: paths.throttled, element: <RateLimitedPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

const routerFutureFlags = {
  // React Router deferrals can leave guard redirects pending in Jest/jsdom.
  v7_startTransition: process.env.NODE_ENV !== 'test',
  v7_relativeSplatPath: true,
};

const routerOptions = {
  future: routerFutureFlags,
};

export function createTestRouter(route = '/') {
  return createMemoryRouter(appRouteObjects, { initialEntries: [route], ...routerOptions });
}

const browserRouter = createBrowserRouter(appRouteObjects, routerOptions);

export default function App({ router = browserRouter }) {
  return (
    <ErrorBoundary>
      <RouterProvider router={router} future={routerFutureFlags} />
    </ErrorBoundary>
  );
}
